import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Formik, useField } from "formik";
import { $Row, $Col, $Input, $Button, $Form, $SelectGroup } from "../antd";
import { useTranslation } from "react-i18next";
import * as Action from "us.common/actions";
import { validationSchema } from "./Validation";
import { AsyncInputCode } from "./Component";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { PostalCodesActions } from "./Component/ItemMenu/constants";

interface IAddPostalArea {
  drawerContentChangeClick: ({ currentStep }: any) => void;
  currentStep: any;
  cancelClick: ({ currentStep }: any) => any;
  municipal: { MunicipalityCode: string; MunicipalityName: string };
  AddPostalFormData: any;
  updateFormData: (data: any) => void;
  resetValidation: (data: any) => void;
  getPostalAreas: any;
  existingCode: any;
  drawerName: string;
  countries: any;
  isSaving: boolean;
  isSaveSuccess: boolean;
  isExisitingCode: boolean;
}

interface IBase extends IAddPostalArea {
  addPostalArea: any;
  newPostalAreaSucessMsg: any;
  onClickSelectMuniciples: () => void;
  onCancelClick: () => void;
  updateDrawerName: (data: string) => void;
  setSaveStatus: (data: boolean) => void;
  countryCode?: string;
  countryCodeField?: string;
  addDrawerVisible?: boolean;
  systemCountryCode?: string;
}

const AddPostalArea: React.FC<IBase> = (props) => {
  const { t } = useTranslation();
  const {
    addPostalArea,
    onCancelClick,
    updateFormData,
    resetValidation,
    updateDrawerName,
    existingCode,
    isSaving,
    countries,
    isSaveSuccess,
    onClickSelectMuniciples,
    setSaveStatus,
    countryCode,
    countryCodeField,
    isExisitingCode,
    addDrawerVisible,
    systemCountryCode
  } = props;

  const { search } = useLocation();
  const [, , countryCodeHelpers] = useField(countryCodeField ?? "standardCode");
  const urlParams = new URLSearchParams(search);
  const action = urlParams.get("action");

  useEffect(() => {
    !addDrawerVisible &&
      updateDrawerName(
        t("US.COLLECTION.COMMON:ENTITYSELECTION.NEW_POSTAL_AREA")
      );

    return () => {
      resetValidation({});
    };
  }, []);

  useEffect(() => {
    if (isSaveSuccess) {
      onCancelClick();
    }
    setSaveStatus(false);
  }, [isSaveSuccess]);

  const handlePostalSubmit = (
    postalData: any,
    { setErrors, setStatus, resetForm, setSubmitting, ...rest }: any
  ) => {
    let saveParams = {
      postalCodeId:
        action === PostalCodesActions.EDIT ? existingCode?.postalCodeId : -1,
      postalCode: postalData.postalCode,
      postalPlace: postalData.postalPlace,
      country: "",
      countyName: postalData.municipalityName,
      countyCode: postalData.municipalityCode,
      countryCode: addDrawerVisible
        ? postalData.countryCode
        : countryCode
        ? countryCode
        : systemCountryCode,
    };
    addPostalArea({ saveParams, addDrawerVisible });
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{
        ...existingCode,
        countryCode: systemCountryCode,
        systemCountryCode
      }}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnChange
      validateOnBlur
      validateOnMount
      onSubmit={(values: any, actions: any) => {
        actions.setSubmitting(true);
        handlePostalSubmit(values, actions);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        setValues,
        ...rest
      }: any) => (
        <>
          <$Form name="newAddPostalArea" onSubmit={handleSubmit}>
            {addDrawerVisible && (
              <$Row gutter={16} className="mb-3">
                <$Col span={24}>
                  <$SelectGroup
                    name="countryCode"
                    formitem={{
                      label: t("US.COLLECTION.COMMON:ENTITYSELECTION.COUNTRY"),
                    }}
                    size="small"
                    virtual={false}
                    value={values?.countryCode}
                    loading={countries?.isLoading}
                    disabled={countries?.isLoading}
                    optionFilterProp="children"
                    filterOption={(input: any, option: any) =>
                      option.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    required
                    options={_.orderBy(
                      countries.data?.map(({ continent, name, code }: any) => ({
                        name: continent,
                        label: name,
                        value: code,
                      })),
                      ["name", "label"],
                      ["asc", "asc"]
                    )}
                    onChange={(value: any) => {
                      countryCodeHelpers.setValue(value);
                      setValues((previousValues: any) => ({
                        ...previousValues,
                        countryCode: value,
                        postalCode:
                          action === PostalCodesActions.EDIT
                            ? previousValues?.postalCode
                            : "",
                        postalPlace: "",
                        municipalityName: "",
                        municipalityCode: "",
                      }));
                      resetValidation({});
                    }}
                  />
                </$Col>
              </$Row>
            )}
            <$Row gutter={16} className="mb-3">
              <$Col span={12}>
                <AsyncInputCode
                  name="postalCode"
                  size="small"
                  label={t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALCODE")}
                  className="mr-0"
                  maxLength={20}
                  countryCode={
                    addDrawerVisible
                      ? values?.countryCode
                      : countryCode ?? systemCountryCode
                  }
                  disabled={
                    action === PostalCodesActions.EDIT ||
                    (addDrawerVisible
                      ? values?.countryCode
                        ? false
                        : true
                      : false)
                  }
                />
              </$Col>
              <$Col span={12}>
                <$Button
                  className="mb-n2 p-0"
                  onClick={() => {
                    onClickSelectMuniciples(), updateFormData(values);
                  }}
                  data-testid="munucipality-link"
                  type="link"
                  disabled={
                    isExisitingCode ||
                    (addDrawerVisible
                      ? values?.countryCode
                        ? false
                        : true
                      : false)
                  }
                >
                  <span className="required mr-1">*</span>
                  <strong>
                    {t("US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYNAME")}
                  </strong>
                </$Button>
                <$Input
                  required
                  size="small"
                  name="municipalityName"
                  disabled
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-3">
              <$Col span={12}>
                <$Input
                  required
                  size="small"
                  name="postalPlace"
                  label={t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALAREA")}
                  className="mr-0"
                />
              </$Col>
              <$Col span={12}>
                <$Input
                  size="small"
                  name="municipalityCode"
                  disabled
                  label={t(
                    "US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYCODE"
                  )}
                  className="bui-label"
                />
              </$Col>
            </$Row>

            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <$Button
                className="mr-2"
                type="primary"
                disabled={!rest.isValid || isExisitingCode || isSaving}
                loading={isSubmitting || isValidating || isSaving}
                htmlType="submit"
                onClick={(e: any) => handleSubmit(e)}
                id="save-btn"
              >
                {t("US.COMMON:COMMON.SAVE")}
              </$Button>
              <$Button onClick={() => onCancelClick()}>
                {t("US.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: any) => {
  const { entity, entityDetails, common } = state;
  const { newPostalAreaSucessMsg } = entity;
  const { systemCountryCode } = common;

  const {
    AddPostalFormData,
    existingCode,
    drawerName,
    countries,
    isSaving,
    isSaveSuccess,
    isExisitingCode,
  } = entityDetails;
  return {
    newPostalAreaSucessMsg,
    AddPostalFormData,
    existingCode,
    drawerName,
    countries,
    isSaving,
    isSaveSuccess,
    isExisitingCode,
    systemCountryCode
  };
};

const {
  addPostalArea,
  PostalCodeFormData,
  getPostalAreas,
  setSaveStatus,
  ChangePostalDrawerTitle,
  postalCodeAvailability,
} = Action.entity;

const mapDispatchToProps = {
  addPostalArea,
  getPostalAreas,
  setSaveStatus,
  updateFormData: PostalCodeFormData,
  updateDrawerName: ChangePostalDrawerTitle,
  resetValidation: postalCodeAvailability.reset,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddPostalArea);
